import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import CloseIcon from '@material-ui/icons/Close'


import TextField from '@material-ui/core/TextField';

import Autocomplete from '@material-ui/lab/Autocomplete'
import CircularProgress from '@material-ui/core/CircularProgress'

import { debounce, kebabCase, camelCase } from 'lodash'

import {FormInputError} from "@nzpost/react-components"


import styles from './AutoComplete.module.css'
import InputAdornment from '@material-ui/core/InputAdornment'
import {withStyles} from '@material-ui/core/styles'

const StyledAutoComplete = withStyles((theme) => ({
  endAdornment: {
    top: "inherit"
  }
}))(Autocomplete)

export const propTypes = {
  /** The value used for the id of the field */
  name: PropTypes.string,
  /** The value to be populated in the field (used when controlled by external state) */
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** The error (if any) to be displayed for the field */
  error: PropTypes.string,
  /** The suggestions to display */
  suggestions: PropTypes.arrayOf(PropTypes.any).isRequired,
  /** If the field has been left and the error should be displayed */
  clicked: PropTypes.bool,
  disabled: PropTypes.bool,
  /** Callback fired when the value changes.
   * This callback is when suggestions should be updated
   * Signature:
   * function(event: object) => void
   * event: The event source of the callback
   */
  onInputChange: PropTypes.func.isRequired,
  /** Callback fired when a value is selected from the dropdown or clear icon is selected.
   * Signature:
   * function(event: object) => void
   * event: The event source of the callback
   */
  onChange: PropTypes.func.isRequired,
  /** Callback fired when the blur event occurs
   * Signature:
   * function(event: object) => void
   * event: The event source of the callback
   */
  onBlur: PropTypes.func.isRequired,
  /** Callback fired when the dropdown is closed
   * Signature:
   * function(event: object) => void
   * event: The event source of the callback
   */
  onClose: PropTypes.func,
  /** The label to be used for the field */
  label: PropTypes.string,
  /** Callback fired when the focus event occurs
   * Signature:
   * function(event: object) => void
   * event: The event source of the callback
   */
  onFocus: PropTypes.func,
  /** The icon used for the close button */
  closeIcon: PropTypes.node,
  autoCompleteIcon: PropTypes.node,
  /** Passed to clearIndication class @see https://material-ui.com/api/autocomplete/#css */
  clearIndicatorClass: PropTypes.string,
  /** What should be displayed when there is no options */
  noOptionsText: PropTypes.node,
  /** Passed to material ui free solo field */
  freeSolo: PropTypes.bool,
  /** Paramater to disable autocomplete for different fields */
  autoCompleteParam: PropTypes.string,
  groupBy: PropTypes.func,
  getOptionLabel: PropTypes.func,
  filterOptions: PropTypes.func,
  displayLoading: PropTypes.bool,
  autoCompleteRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  })
}

export const defaultProps = {
  label: null,
  defaultValue: null,
  name: 'default',
  clicked: false,
  disabled: false,
  error: '',
  onClose: () => {},
  onFocus: () => {},
  closeIcon: <CloseIcon fontSize={"small"} />,
  autoCompleteIcon: undefined,
  clearIndicatorClass: '',
  noOptionsText: undefined,
  freeSolo: true,
  autoCompleteParam: 'off',
  groupBy: () => {},
  getOptionLabel: undefined,
  filterOptions: undefined,
  displayLoading: false,
  autoCompleteRef: null,
  margin: "normal",
  helperText: ""
}

/**
 * A wrapper around matarial ui Autocomplete
 *
 * @see https://material-ui.com/api/autocomplete/
 */
export default function NZPAutoComplete({
  name,
  error,
  suggestions,
  onInputChange,
  onBlur,
  onClose,
  onChange,
  clicked,
  disabled,
  defaultValue,
  label,
  onFocus,
  closeIcon,
  autoCompleteIcon,
  clearIndicatorClass,
  noOptionsText,
  freeSolo,
  autoCompleteParam,
  groupBy,
  getOptionLabel,
  filterOptions,
  displayLoading,
  autoCompleteRef,
  placeholder,
  shrink,
  margin,
  helperText,
  inputRef,
  value
}) {

  const [isFocused, setIsFocused] = useState(false)
  const [initValue] = useState(defaultValue)
  const debounceOnInputChange = useCallback(debounce(onInputChange, 300), [])

  return (
    <StyledAutoComplete
      value={value}
      id={`${kebabCase(name)}-field`}
      classes={{
        endAdornment: styles['end-adornment'],
        clearIndicator: clearIndicatorClass,
        groupLabel: styles.groupLabel,
        option: styles.option
      }}
      disabled={disabled}
      ref={autoCompleteRef}
      filterOptions={filterOptions}
      closeIcon={closeIcon}
      freeSolo={freeSolo}
      defaultValue={initValue}
      options={suggestions}
      groupBy={groupBy}
      getOptionLabel={getOptionLabel}
      onInputChange={onInputChange}
      onChange={onChange}
      noOptionsText={noOptionsText}
      onClose={() => {
        onClose()
      }}
      onFocus={() => setIsFocused(true)}
      onBlur={(e) => {
        onBlur(e)
        setIsFocused(false)
      }}
      renderInput={(params) => {
        const { InputProps, InputLabelProps, inputProps } = params;
        InputLabelProps.shrink = shrink;
        const newInputLabelProps = {
          ...InputLabelProps,
          classes: {
            root: autoCompleteIcon
              ? styles['form-label-search']
              : styles['form-label'],
            focused: styles['form-label-focused'],
            error: styles['form-label-error'],
            outlined: styles['form-label-outline'],
            shrink: styles['form-label-shrink']
          }
        }
        const newInputProps = {
          ...InputProps,
          classes: {
            root: autoCompleteIcon
              ? styles['form-input-root-search']
              : styles['form-input-root'],
            input: styles['form-input'],
            notchedOutline: isFocused
              ? styles['form-input-focused']
              : styles['form-input-outline']
          },
          ref: inputRef
        }

        if (autoCompleteIcon) {
          newInputProps.endAdornment = (
            <InputAdornment position='end'>{autoCompleteIcon}</InputAdornment>
          )
        }

        if (displayLoading) {
          newInputProps.endAdornment = (
            <InputAdornment className={styles.inputAdornment}>
              <CircularProgress size='20px' />
            </InputAdornment>
          )
        }

        const newinputProps = {
          ...inputProps,
          autoComplete: autoCompleteParam
        }

        const newParams = {
          ...params,
          InputLabelProps: newInputLabelProps,
          InputProps: newInputProps,
          inputProps: newinputProps
        }
        console.log('autocomplete render', {newParams, params});
        // newParams.inputProps.value = "hello world";

        return (
          <div>
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...newParams}
              color="secondary"
              label={label}
              placeholder={isFocused ? '':placeholder}
              name={camelCase(name)}
              // className={styles['form-input-container']}
              margin={margin}
              variant='outlined'
              error={!!(error && clicked)}
              height='56px'
              fullWidth
              // eslint-disable-next-line react/jsx-no-duplicate-props
              onFocus={onFocus}
              helperText={helperText}
              ref={params.InputProps.ref}
            />
          </div>
        )
      }}
    />
  )
}

NZPAutoComplete.propTypes = propTypes
NZPAutoComplete.defaultProps = defaultProps
